import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

import { ITEM_TYPE_INFO_MAP } from 'Angular/features/components/saved-items/open-item-modal/constants';
import { SCATTER_PLOT_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { SavedItemType } from 'Angular/features/components/saved-items/enums/saved-item-type.enum';
import { OpenItemModalAction } from 'Angular/features/components/saved-items/open-item-modal/enums/open-item-modal-action.enum';
import { FormattedShareableItem } from 'Angular/features/components/saved-items/open-item-modal/interfaces/serialized-shareable-item.interface';
import { ShareableItemTypeInfo } from 'Angular/features/components/saved-items/open-item-modal/interfaces/shareable-item-type-info-map.interface';
import { ShareableItem } from 'SharingApp/ts/interfaces/shareable-item.interface';

import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';

import { FeatureNeededComponent } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.component';
import equal from 'Angular/utils/equal/deep-equal';

/**
 * Open Item Modal Component
 *
 * this modal is used for opening saved items of various types such as metric sets, fund charts, company screens,
 * comp tables, fund screens, scoring models, and time series tables
 *
 * Input Properties:
 *  items: ShareableItem[] to be displayed in the modal
 *  selectOnly: boolean whether items should be able to be edited or deleted
 *  savedItemType: SavedItemType type of the items being displayed
 *
 * Output Properties:
 *  itemDeletionHandler: EventEmitter<number> that emits the id if an item is deleted
 *  itemSelectionHandler: EventEmitter<ShareableItem> that emits the item if one is selected
 *  showEditModalHandler: EventEmitter<ShareableItem> that emits the item if one is edited
 */
@Component({ standalone: false, selector: 'ycn-open-item-modal', templateUrl: './open-item-modal.template.html' })
export class OpenItemModalComponent implements OnChanges, OnInit {
    @Input() items!: ShareableItem[];

    @Input() selectOnly: boolean = false;

    @Input() savedItemType!: SavedItemType;

    @Input() sharedWithMeEnabled: boolean = true;

    @Input() errorMessage!: string;

    @Output() itemDeletionHandler = new EventEmitter<number>();

    @Output() itemSelectionHandler = new EventEmitter<FormattedShareableItem>();

    @Output() showEditModalHandler = new EventEmitter<FormattedShareableItem>();

    @ViewChild(FeatureNeededComponent) featureNeededComponent!: FeatureNeededComponent;

    ownItems: ShareableItem[] = [];

    sharedItems: ShareableItem[] = [];

    itemTypeInfo: ShareableItemTypeInfo | null = null;

    loading: boolean = false;

    modalOpen: boolean = false;

    saveItemsLabel: string = '';

    constructor(private featureService: FeatureService) {}

    ngOnInit(): void {
        this.setItemTypeInfo(this.savedItemType);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.items && changes.items.currentValue) {
            // Filter items into own items and shared items when items change
            const newOwnItems = this.items.filter((item) => !item.sharing || item.sharing.is_owner);
            if (!equal(this.ownItems, newOwnItems)) this.ownItems = newOwnItems;

            const newSharedItems = this.items.filter((item) => item.sharing && !item.sharing.is_owner);
            if (!equal(this.sharedItems, newSharedItems)) this.sharedItems = newSharedItems;

            this.setItemTypeInfo(this.savedItemType);
        }
    }

    doActionOnItem(itemAndAction: { item: FormattedShareableItem | null; actionType: OpenItemModalAction }): void {
        if (itemAndAction.item) {
            switch (itemAndAction.actionType) {
                case OpenItemModalAction.SELECT:
                    this.closeModal();
                    this.itemSelectionHandler.emit(itemAndAction.item);
                    break;
                case OpenItemModalAction.EDIT:
                    this.closeModal();
                    this.showEditModalHandler.emit(itemAndAction.item);
                    break;
                case OpenItemModalAction.DELETE:
                    this.itemDeletionHandler.emit(itemAndAction.item.id);
                    break;
                default:
                    break;
            }
        }
    }

    setItemTypeInfo(itemType: SavedItemType): void {
        this.itemTypeInfo = ITEM_TYPE_INFO_MAP[itemType] as ShareableItemTypeInfo;
        if (Object.keys(this.itemTypeInfo).length) {
            this.saveItemsLabel = `Open Saved ${this.itemTypeInfo.singularLabel}`;
        }
    }

    closeModal(): void {
        this.modalOpen = false;
    }

    openModal(): void {
        if (
            this.savedItemType === SavedItemType.SCATTER_PLOT &&
            !this.featureService.userHasFeatures([SCATTER_PLOT_FEATURE])
        ) {
            this.featureNeededComponent.openModal(SCATTER_PLOT_FEATURE);
        } else {
            this.modalOpen = true;
        }
    }
}
